import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

type ChatUser = {
  id: number;
  username: string;
  email: string;
  role: string;
  department: string;
  profile_image?: string | null;
  latest_message_at?: string | null;
};

type LoggedInUser = {
  id?: number;
  username?: string;
  email?: string;
  role?: string;
  department?: string;
  profileImage?: string;
  profile_image?: string;
};

type ChatMessage = {
  id: number;
  sender_id: number;
  receiver_id: number;
  message: string;
  is_read?: boolean;
  created_at?: string;
  attachment_url?: string | null;
  attachment_name?: string | null;
  attachment_type?: string | null;
  attachment_preview_url?: string | null;
  attachment_preview_pdf_url?: string | null;
};

type UnreadCounts = {
  [senderId: string]: number;
};

type PreviewFile = {
  url: string;
  name: string;
};

const EmployeeChatPage: React.FC = () => {
  const navigate = useNavigate();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const pendingInitialScrollTargetRef = useRef<number | "end" | null>(null);

  const [message, setMessage] = useState("");
  const [users, setUsers] = useState<ChatUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<ChatUser | null>(null);
  const [searchText, setSearchText] = useState("");
  const [loggedInUser, setLoggedInUser] =
    useState<LoggedInUser | null>(null);
  const [employeeProfileImage, setEmployeeProfileImage] = useState("");
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<UnreadCounts>({});
  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);

  const fetchUnreadCounts = (userId: number) => {
    fetch(
      `/chat/unread-counts?user_id=${userId}`
    )
      .then((response) => response.json())
      .then((data: UnreadCounts) => {
        setUnreadCounts(data);
      })
      .catch((error) => {
        console.error("Unread counts fetch error:", error);
      });
  };

  const updateUserLatestMessage = (
  userId: number,
  createdAt?: string
) => {
  const latest = createdAt || new Date().toISOString();

  setUsers((prevUsers) =>
    prevUsers.map((user) =>
      user.id === userId
        ? {
            ...user,
            latest_message_at: latest,
          }
        : user
    )
  );
};

const fetchChatUsers = (currentUserId: number) => {
  fetch(
    `/chat/users?current_user_id=${currentUserId}`
  )
    .then((response) => response.json())
    .then((data: ChatUser[]) => {
      const otherUsers = data.filter(
        (chatUser) => chatUser.id !== currentUserId
      );

      setUsers(otherUsers);
    })
    .catch((error) => {
      console.error("Chat users refresh error:", error);
    });
};

  const fetchConversation = (
    currentUserId: number,
    selectedUserId: number,
    setInitialPosition = false
  ) => {
    fetch(
      `/chat/conversation?user1_id=${currentUserId}&user2_id=${selectedUserId}`
    )
      .then((response) => response.json())
      .then((data: ChatMessage[]) => {
        if (setInitialPosition) {
          const firstUnreadMessage = data.find(
            (chatMessage) =>
              chatMessage.sender_id === selectedUserId &&
              chatMessage.receiver_id === currentUserId &&
              chatMessage.is_read === false
          );

          pendingInitialScrollTargetRef.current = firstUnreadMessage
            ? firstUnreadMessage.id
            : "end";
        }

        setChatMessages(data);

        return fetch(
          `/chat/mark-read?sender_id=${selectedUserId}&receiver_id=${currentUserId}`,
          {
            method: "PUT",
          }
        );
      })
      .then(() => {
        fetchUnreadCounts(currentUserId);

        return fetch(
          `/chat/conversation?user1_id=${currentUserId}&user2_id=${selectedUserId}`
        );
      })
      .then((response) => response.json())
      .then((updatedData: ChatMessage[]) => {
        setChatMessages(updatedData);
      })
      .catch((error) => {
        console.error("Conversation or mark-read error:", error);
      });
  };

  useEffect(() => {
    const storedUser = sessionStorage.getItem("loggedInUser");

    if (storedUser) {
      const user: LoggedInUser = JSON.parse(storedUser);

      setLoggedInUser(user);

      const imagePath =
        user.profile_image || user.profileImage || "";

      setEmployeeProfileImage(
        imagePath && imagePath.startsWith("/uploads")
          ? `${imagePath}`
          : imagePath
      );

      if (user.id) {
        fetchUnreadCounts(user.id);
      }

      fetch(
        `/chat/users?current_user_id=${user.id}`
      )
        .then((response) => response.json())
        .then((data: ChatUser[]) => {
          const otherUsers = data.filter(
            (chatUser) => chatUser.id !== user.id
          );

          const sortedUsers = [...otherUsers].sort((a, b) => {
            const latestTimeA = a.latest_message_at
              ? new Date(a.latest_message_at).getTime()
              : 0;

            const latestTimeB = b.latest_message_at
              ? new Date(b.latest_message_at).getTime()
              : 0;

            if (latestTimeA !== latestTimeB) {
              return latestTimeB - latestTimeA;
            }

            return a.username.localeCompare(
              b.username,
              "ja"
            );
          });

          setUsers(sortedUsers);

          if (sortedUsers.length > 0) {
            setSelectedUser(sortedUsers[0]);
          }
        })
        .catch((error) => {
          console.error("Chat users fetch error:", error);
        });
    }
  }, []);


  useEffect(() => {
  if (!loggedInUser?.id) {
    return;
  }

  const currentUserId = loggedInUser.id;

  const refreshChatList = () => {
    fetchChatUsers(currentUserId);
    fetchUnreadCounts(currentUserId);
  };

  const intervalId = window.setInterval(() => {
    refreshChatList();
  }, 3000);

  return () => {
    window.clearInterval(intervalId);
  };
}, [loggedInUser?.id]);

  useEffect(() => {
    if (!loggedInUser?.id || !selectedUser?.id) {
      return;
    }


    

    setChatMessages([]);
    pendingInitialScrollTargetRef.current = null;

    fetchConversation(
      loggedInUser.id,
      selectedUser.id,
      true
    );

    const intervalId = window.setInterval(() => {
      fetchConversation(
        loggedInUser.id!,
        selectedUser.id,
        false
      );
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loggedInUser?.id, selectedUser?.id]);

  useEffect(() => {
    const scrollTarget =
      pendingInitialScrollTargetRef.current;

    if (
      scrollTarget === null ||
      chatMessages.length === 0
    ) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (scrollTarget === "end") {
        messagesEndRef.current?.scrollIntoView({
          behavior: "auto",
          block: "end",
        });
      } else {
        const unreadMessageElement =
          document.getElementById(
            `chat-message-${scrollTarget}`
          );

        unreadMessageElement?.scrollIntoView({
          behavior: "auto",
          block: "start",
        });
      }

      pendingInitialScrollTargetRef.current = null;
    });
  }, [chatMessages]);

  const handleSelectUser = (user: ChatUser) => {
    setSelectedUser(user);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = event.target.files?.[0];

    if (
      !selectedFile ||
      !loggedInUser?.id ||
      !selectedUser?.id
    ) {
      event.target.value = "";
      return;
    }

    const formData = new FormData();

    formData.append("file", selectedFile);
    formData.append(
      "sender_id",
      String(loggedInUser.id)
    );
    formData.append(
      "receiver_id",
      String(selectedUser.id)
    );

    fetch("/chat/upload", {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then((newMessage: ChatMessage) => {
        setChatMessages((prevMessages) => [
          ...prevMessages,
          newMessage,
        ]);

        updateUserLatestMessage(
  selectedUser.id,
  newMessage.created_at
);

        window.requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({
            behavior: "auto",
            block: "end",
          });
        });
      })
      .catch((error) => {
        console.error("File upload error:", error);
      })
      .finally(() => {
        event.target.value = "";
      });
  };

  const handleSendMessage = () => {
    const trimmedMessage = message.trim();

    if (
      !trimmedMessage ||
      !loggedInUser?.id ||
      !selectedUser?.id
    ) {
      return;
    }

    fetch(
      `/chat/send?sender_id=${loggedInUser.id}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          receiver_id: selectedUser.id,
          message: trimmedMessage,
        }),
      }
    )
      .then((response) => response.json())
      .then((newMessage: ChatMessage) => {
        setChatMessages((prevMessages) => [
          ...prevMessages,
          newMessage,
        ]);

        setMessage("");

        updateUserLatestMessage(
  selectedUser.id,
  newMessage.created_at
);

        window.requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({
            behavior: "auto",
            block: "end",
          });
        });
      })
      .catch((error) => {
        console.error("Message send error:", error);
      });
  };

  const goToEmployeeMenu = (
    menu:
      | "dashboard"
      | "courses"
      | "tests"
      | "notifications"
  ) => {
    localStorage.setItem(
      "employeeDashboardActiveMenu",
      menu
    );

    navigate("/employee-dashboard");
  };

  const handleLogout = () => {
    sessionStorage.removeItem("loggedInUser");
    navigate("/login");
  };

  const filteredUsers = users
    .filter((user) => {
      const keyword = searchText.toLowerCase();

      return (
        user.username
          .toLowerCase()
          .includes(keyword) ||
        user.email.toLowerCase().includes(keyword) ||
        user.department
          .toLowerCase()
          .includes(keyword)
      );
    })
    .sort((a, b) => {
      const latestTimeA = a.latest_message_at
        ? new Date(a.latest_message_at).getTime()
        : 0;

      const latestTimeB = b.latest_message_at
        ? new Date(b.latest_message_at).getTime()
        : 0;

      if (latestTimeA !== latestTimeB) {
        return latestTimeB - latestTimeA;
      }

      const unreadA =
        unreadCounts[String(a.id)] || 0;

      const unreadB =
        unreadCounts[String(b.id)] || 0;

      if (unreadA !== unreadB) {
        return unreadB - unreadA;
      }

      return a.username.localeCompare(
        b.username,
        "ja"
      );
    });

  const getRoleLabel = (role: string) => {
    if (role === "admin") return "管理者";
    if (role === "employee") return "社員";

    return role;
  };

  const getAvatarText = (username: string) => {
    return username
      ? username.charAt(0).toUpperCase()
      : "U";
  };

  const getProfileImageUrl = (
    profileImage?: string | null
  ) => {
    if (!profileImage) return "";

    if (profileImage.startsWith("http")) {
      return profileImage;
    }

    if (profileImage.startsWith("/uploads")) {
      return `${profileImage}`;
    }

    return profileImage;
  };

  const getAttachmentUrl = (
    url?: string | null
  ) => {
    if (!url) return "";

    if (url.startsWith("http")) {
      return url;
    }

    if (url.startsWith("/uploads")) {
      return `${url}`;
    }

    return url;
  };

  const getAttachmentPreviewUrl = (
    url?: string | null
  ) => {
    return getAttachmentUrl(url);
  };

  const isImageAttachment = (
    attachmentType?: string | null
  ) => {
    if (!attachmentType) return false;

    const type = attachmentType.toLowerCase();

    return (
      type.startsWith("image/") ||
      ["jpg", "jpeg", "png", "gif", "webp"].includes(
        type
      )
    );
  };

  const formatDateSeparator = (
    createdAt?: string
  ) => {
    if (!createdAt) return "";

    const date = new Date(createdAt);

    return date.toLocaleDateString("ja-JP", {
      month: "long",
      day: "numeric",
      weekday: "long",
    });
  };

  const formatMessageTime = (
    createdAt?: string
  ) => {
    if (!createdAt) return "";

    const date = new Date(createdAt);

    return date.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const shouldShowDateSeparator = (
    currentMessage: ChatMessage,
    previousMessage?: ChatMessage
  ) => {
    if (!currentMessage.created_at) {
      return false;
    }

    if (!previousMessage?.created_at) {
      return true;
    }

    const currentDate = new Date(
      currentMessage.created_at
    ).toDateString();

    const previousDate = new Date(
      previousMessage.created_at
    ).toDateString();

    return currentDate !== previousDate;
  };

  const isImageUrl = (url: string) => {
    const lowerUrl = url.toLowerCase();

    return (
      lowerUrl.endsWith(".jpg") ||
      lowerUrl.endsWith(".jpeg") ||
      lowerUrl.endsWith(".png") ||
      lowerUrl.endsWith(".gif") ||
      lowerUrl.endsWith(".webp")
    );
  };

  const openFilePreview = (
    chatMessage: ChatMessage
  ) => {
    const fileUrl =
      chatMessage.attachment_preview_pdf_url
        ? getAttachmentUrl(
            chatMessage.attachment_preview_pdf_url
          )
        : chatMessage.attachment_preview_url
        ? getAttachmentPreviewUrl(
            chatMessage.attachment_preview_url
          )
        : getAttachmentUrl(
            chatMessage.attachment_url
          );

    const fileName =
      chatMessage.attachment_name ||
      chatMessage.message ||
      "ファイル";

    if (!fileUrl) return;

    setPreviewFile({
      url: fileUrl,
      name: fileName,
    });
  };

  const displayName =
    loggedInUser?.username || "社員ユーザー";

  const displayDepartment =
    loggedInUser?.department || "社員";

  const totalUnreadCount = Object.values(
    unreadCounts
  ).reduce(
    (total, count) => total + count,
    0
  );

  return (
    <div style={styles.page}>
      <aside style={styles.sidebar}>
        <div>
          <div style={styles.logoArea}>
            <div style={styles.logoIcon}>🎓</div>
            <div style={styles.logoText}>
              LearnHub
            </div>
          </div>

          <div style={styles.menuSectionTitle}>
            LEARNING
          </div>

          <nav style={styles.nav}>
            <button
              type="button"
              onClick={() =>
                goToEmployeeMenu("dashboard")
              }
              style={styles.navItem}
            >
              ▦ ダッシュボード
            </button>

            <button
              type="button"
              onClick={() =>
                goToEmployeeMenu("courses")
              }
              style={styles.navItem}
            >
              📖 マイコース
            </button>

            <button
              type="button"
              onClick={() =>
                goToEmployeeMenu("tests")
              }
              style={styles.navItem}
            >
              📝 テスト
            </button>

            <button
              type="button"
              onClick={() =>
                goToEmployeeMenu("notifications")
              }
              style={styles.navItem}
            >
              🔔 通知
            </button>

            <button
              type="button"
              style={styles.activeNavItem}
            >
              <span>💬 チャット</span>

              {totalUnreadCount > 0 && (
                <span style={styles.navBadge}>
                  {totalUnreadCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() =>
                navigate("/employee-settings")
              }
              style={styles.navItem}
            >
              ⚙️ 設定
            </button>
          </nav>
        </div>

        <div style={styles.profileBottomArea}>
          <div style={styles.profileArea}>
            <div style={styles.profileAvatar}>
              {employeeProfileImage ? (
                <img
                  src={employeeProfileImage}
                  alt="プロフィール"
                  style={styles.avatarImage}
                />
              ) : (
                displayName
                  .charAt(0)
                  .toUpperCase()
              )}
            </div>

            <div>
              <div style={styles.profileName}>
                {displayName}
              </div>

              <div style={styles.profileDept}>
                {displayDepartment}
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                navigate("/employee-settings")
              }
              style={styles.settingButton}
            >
              ⚙
            </button>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            style={styles.logoutButton}
          >
            ログアウト
          </button>
        </div>
      </aside>

      <main style={styles.main}>
        <header style={styles.header}>
          <div>
            <h1 style={styles.pageTitle}>
              チャット
            </h1>

            <p style={styles.pageSubtitle}>
              ログインユーザーとメッセージを送受信できます
            </p>
          </div>
        </header>

        <div style={styles.container}>
          <aside style={styles.userPanel}>
            <h2 style={styles.panelTitle}>
              ユーザー一覧
            </h2>

            <div style={styles.panelSearchBox}>
              <span style={styles.searchIcon}>
                🔍
              </span>

              <input
                type="text"
                placeholder="名前・メール・部署で検索"
                value={searchText}
                onChange={(event) =>
                  setSearchText(event.target.value)
                }
                style={styles.searchInput}
              />
            </div>

            <div style={styles.userList}>
              {filteredUsers.length === 0 ? (
                <div style={styles.emptyText}>
                  ユーザーが見つかりません
                </div>
              ) : (
                filteredUsers.map((user) => {
                  const isActive =
                    selectedUser?.id === user.id;

                  const profileImageUrl =
                    getProfileImageUrl(
                      user.profile_image
                    );

                  const userUnreadCount =
                    unreadCounts[
                      String(user.id)
                    ] || 0;

                  return (
                    <div
                      key={user.id}
                      onClick={() =>
                        handleSelectUser(user)
                      }
                      style={
                        isActive
                          ? styles.userItemActive
                          : styles.userItem
                      }
                    >
                      <div style={styles.avatar}>
                        {profileImageUrl ? (
                          <img
                            src={profileImageUrl}
                            alt="プロフィール"
                            style={
                              styles.avatarImage
                            }
                          />
                        ) : (
                          getAvatarText(
                            user.username
                          )
                        )}
                      </div>

                      <div style={styles.userInfo}>
                        <div
                          style={styles.userNameRow}
                        >
                          <div
                            style={styles.userName}
                          >
                            {user.username}
                          </div>

                          {userUnreadCount > 0 && (
                            <span
                              style={
                                styles.unreadBadge
                              }
                            >
                              {userUnreadCount}
                            </span>
                          )}
                        </div>

                        <div
                          style={styles.userRole}
                        >
                          {getRoleLabel(user.role)} ・{" "}
                          {user.department}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </aside>

          <section style={styles.chatPanel}>
            <header style={styles.chatHeader}>
              <div
                style={styles.chatHeaderUser}
              >
                <div
                  style={
                    styles.chatHeaderAvatar
                  }
                >
                  {selectedUser &&
                  getProfileImageUrl(
                    selectedUser.profile_image
                  ) ? (
                    <img
                      src={getProfileImageUrl(
                        selectedUser.profile_image
                      )}
                      alt="プロフィール"
                      style={styles.avatarImage}
                    />
                  ) : selectedUser ? (
                    getAvatarText(
                      selectedUser.username
                    )
                  ) : (
                    "U"
                  )}
                </div>

                <div>
                  <h3 style={styles.chatTitle}>
                    {selectedUser
                      ? selectedUser.username
                      : "ユーザーを選択してください"}
                  </h3>

                  <p
                    style={styles.chatSubTitle}
                  >
                    {selectedUser
                      ? `${getRoleLabel(
                          selectedUser.role
                        )} ・ ${
                          selectedUser.department
                        }`
                      : "会話を開始するユーザーを選択してください"}
                  </p>
                </div>
              </div>
            </header>

            <section style={styles.messages}>
              {chatMessages.length === 0 ? (
                <div style={styles.emptyText}>
                  メッセージはまだありません
                </div>
              ) : (
                chatMessages.map(
                  (chatMessage, index) => {
                    const isMyMessage =
                      chatMessage.sender_id ===
                      loggedInUser?.id;

                    const previousMessage =
                      index > 0
                        ? chatMessages[index - 1]
                        : undefined;

                    const showDateSeparator =
                      shouldShowDateSeparator(
                        chatMessage,
                        previousMessage
                      );

                    return (
                      <React.Fragment
                        key={chatMessage.id}
                      >
                        {showDateSeparator && (
                          <div
                            style={
                              styles.dateSeparator
                            }
                          >
                            {formatDateSeparator(
                              chatMessage.created_at
                            )}
                          </div>
                        )}

                        <div
                          id={`chat-message-${chatMessage.id}`}
                          style={
                            isMyMessage
                              ? styles.sentMessageWrapper
                              : styles.receivedMessageWrapper
                          }
                        >
                          <div
                            style={
                              styles.messageTime
                            }
                          >
                            {formatMessageTime(
                              chatMessage.created_at
                            )}
                          </div>

                          <div
                            style={
                              chatMessage.attachment_url
                                ? styles.attachmentMessageWrapper
                                : isMyMessage
                                ? styles.sentMessage
                                : styles.receivedMessage
                            }
                          >
                            {chatMessage.attachment_url ? (
                              isImageAttachment(
                                chatMessage.attachment_type
                              ) ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    openFilePreview(
                                      chatMessage
                                    )
                                  }
                                  style={
                                    styles.imageAttachmentCard
                                  }
                                >
                                  <img
                                    src={getAttachmentUrl(
                                      chatMessage.attachment_url
                                    )}
                                    alt={
                                      chatMessage.attachment_name ||
                                      "添付画像"
                                    }
                                    style={
                                      styles.chatImageAttachment
                                    }
                                  />

                                  <div
                                    style={
                                      styles.imageFileName
                                    }
                                  >
                                    {chatMessage.attachment_name ||
                                      chatMessage.message}
                                  </div>
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() =>
                                    openFilePreview(
                                      chatMessage
                                    )
                                  }
                                  style={
                                    styles.documentAttachmentCard
                                  }
                                >
                                  <div
                                    style={
                                      styles.documentPreviewArea
                                    }
                                  >
                                    {chatMessage.attachment_preview_url ? (
                                      <img
                                        src={getAttachmentPreviewUrl(
                                          chatMessage.attachment_preview_url
                                        )}
                                        alt={
                                          chatMessage.attachment_name ||
                                          "ファイルプレビュー"
                                        }
                                        style={
                                          styles.documentPreviewImage
                                        }
                                      />
                                    ) : (
                                      <div
                                        style={
                                          styles.documentPreviewPlaceholder
                                        }
                                      >
                                        <div
                                          style={
                                            styles.documentPreviewIcon
                                          }
                                        >
                                          📄
                                        </div>

                                        <div
                                          style={
                                            styles.documentPreviewText
                                          }
                                        >
                                          プレビュー
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  <div
                                    style={
                                      styles.documentFileInfoBar
                                    }
                                  >
                                    <div
                                      style={
                                        styles.fileIconBox
                                      }
                                    >
                                      📄
                                    </div>

                                    <div
                                      style={
                                        styles.fileInfoBox
                                      }
                                    >
                                      <div
                                        style={
                                          styles.fileNameText
                                        }
                                      >
                                        {chatMessage.attachment_name ||
                                          chatMessage.message}
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              )
                            ) : (
                              chatMessage.message
                            )}
                          </div>

                          {isMyMessage && (
                            <div
                              style={
                                styles.seenStatus
                              }
                            >
                              {chatMessage.is_read
                                ? "既読"
                                : "未読"}
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  }
                )
              )}

              <div ref={messagesEndRef} />
            </section>

            <footer style={styles.inputArea}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />

              <button
                type="button"
                onClick={handleUploadClick}
                style={styles.uploadButton}
              >
                📎
              </button>

              <input
                type="text"
                placeholder="メッセージを入力"
                value={message}
                onChange={(event) =>
                  setMessage(event.target.value)
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleSendMessage();
                  }
                }}
                style={styles.messageInput}
              />

              <button
                type="button"
                onClick={handleSendMessage}
                style={styles.sendButton}
              >
                送信
              </button>
            </footer>
          </section>
        </div>
      </main>

      {previewFile && (
        <div style={styles.previewOverlay}>
          <div style={styles.previewModal}>
            <div style={styles.previewHeader}>
              <div style={styles.previewTitle}>
                {previewFile.name}
              </div>

              <button
                type="button"
                onClick={() =>
                  setPreviewFile(null)
                }
                style={
                  styles.previewCloseButton
                }
              >
                ✕
              </button>
            </div>

            {isImageUrl(previewFile.url) ? (
              <div
                style={styles.previewImageArea}
              >
                <img
                  src={previewFile.url}
                  alt={previewFile.name}
                  style={styles.previewLargeImage}
                />
              </div>
            ) : (
              <iframe
                src={previewFile.url}
                title={previewFile.name}
                style={styles.previewFrame}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles: {
  [key: string]: React.CSSProperties;
} = {
  page: {
    minHeight: "100vh",
    display: "flex",
    backgroundColor: "#f8fafc",
    color: "#0f172a",
    fontFamily: "Arial, sans-serif",
  },

  sidebar: {
    width: "290px",
    minHeight: "100vh",
    backgroundColor: "#14213d",
    color: "#ffffff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flexShrink: 0,
  },

  logoArea: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "22px",
    borderBottom:
      "1px solid rgba(255,255,255,0.12)",
  },

  logoIcon: {
    width: "46px",
    height: "46px",
    borderRadius: "14px",
    backgroundColor: "#2dd4bf",
    color: "#0f172a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
  },

  logoText: {
    fontSize: "20px",
    fontWeight: 800,
  },

  menuSectionTitle: {
    padding: "22px 28px 10px",
    color: "#94a3b8",
    fontSize: "13px",
    fontWeight: 800,
  },

  nav: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "0 12px",
  },

  navItem: {
    width: "100%",
    border: "none",
    backgroundColor: "transparent",
    color: "#cbd5e1",
    textAlign: "left",
    padding: "14px 16px",
    borderRadius: "14px",
    fontSize: "16px",
    fontWeight: 700,
    cursor: "pointer",
  },

  activeNavItem: {
    width: "100%",
    border: "none",
    backgroundColor: "#1e3a68",
    color: "#ffffff",
    textAlign: "left",
    padding: "14px 16px",
    borderRadius: "14px",
    fontSize: "16px",
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  navBadge: {
    minWidth: "22px",
    height: "22px",
    padding: "0 7px",
    borderRadius: "999px",
    backgroundColor: "#ef4444",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: 900,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  profileBottomArea: {
    borderTop:
      "1px solid rgba(255,255,255,0.12)",
    padding: "18px",
  },

  profileArea: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },

  profileAvatar: {
    width: "46px",
    height: "46px",
    borderRadius: "50%",
    backgroundColor: "#dbeafe",
    color: "#1e3a8a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    overflow: "hidden",
    flexShrink: 0,
  },

  profileName: {
    fontSize: "15px",
    fontWeight: 800,
  },

  profileDept: {
    fontSize: "13px",
    color: "#93c5fd",
  },

  settingButton: {
    marginLeft: "auto",
    color: "#cbd5e1",
    border: "none",
    backgroundColor: "transparent",
    fontSize: "18px",
    cursor: "pointer",
  },

  logoutButton: {
    width: "100%",
    marginTop: "14px",
    border: "none",
    backgroundColor:
      "rgba(239, 68, 68, 0.18)",
    color: "#fecaca",
    padding: "12px 14px",
    borderRadius: "12px",
    fontWeight: 900,
    cursor: "pointer",
  },

  main: {
    flex: 1,
    padding: "34px",
    boxSizing: "border-box",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "28px",
  },

  pageTitle: {
    margin: 0,
    fontSize: "30px",
    fontWeight: 800,
    color: "#0f172a",
  },

  pageSubtitle: {
    margin: "10px 0 0",
    fontSize: "15px",
    color: "#64748b",
  },

  container: {
    display: "grid",
    gridTemplateColumns: "340px 1fr",
    gap: "24px",
    height: "calc(100vh - 140px)",
  },

  userPanel: {
    backgroundColor: "#ffffff",
    borderRadius: "22px",
    border: "1px solid #e2e8f0",
    padding: "24px",
    boxShadow:
      "0 10px 24px rgba(15, 23, 42, 0.05)",
    overflowY: "auto",
  },

  panelTitle: {
    margin: "0 0 18px",
    fontSize: "20px",
    fontWeight: 800,
    color: "#0f172a",
  },

  panelSearchBox: {
    height: "44px",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    padding: "0 14px",
    marginBottom: "18px",
  },

  searchIcon: {
    marginRight: "10px",
  },

  searchInput: {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: "14px",
    color: "#334155",
  },

  userList: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },

  userItem: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "16px",
    borderRadius: "18px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    cursor: "pointer",
  },

  userItemActive: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "16px",
    borderRadius: "18px",
    backgroundColor: "#ecfdf5",
    border: "1px solid #99f6e4",
    cursor: "pointer",
  },

  avatar: {
    width: "46px",
    height: "46px",
    borderRadius: "14px",
    backgroundColor: "#ccfbf1",
    color: "#0f766e",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: "16px",
    overflow: "hidden",
    flexShrink: 0,
  },

  avatarImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  userInfo: {
    flex: 1,
    minWidth: 0,
  },

  userNameRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },

  userName: {
    fontSize: "15px",
    fontWeight: 800,
    color: "#0f172a",
  },

  unreadBadge: {
    minWidth: "22px",
    height: "22px",
    padding: "0 7px",
    borderRadius: "999px",
    backgroundColor: "#ef4444",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: 900,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  userRole: {
    fontSize: "13px",
    color: "#64748b",
    marginTop: "4px",
  },

  emptyText: {
    color: "#64748b",
    fontSize: "14px",
    padding: "16px",
    backgroundColor: "#f8fafc",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    alignSelf: "center",
  },

  chatPanel: {
    backgroundColor: "#ffffff",
    borderRadius: "22px",
    border: "1px solid #e2e8f0",
    boxShadow:
      "0 10px 24px rgba(15, 23, 42, 0.05)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },

  chatHeader: {
    padding: "22px 28px",
    borderBottom: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
  },

  chatHeaderUser: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },

  chatHeaderAvatar: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    backgroundColor: "#ccfbf1",
    color: "#0f766e",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: "16px",
    overflow: "hidden",
    flexShrink: 0,
  },

  chatTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: 800,
    color: "#0f172a",
  },

  chatSubTitle: {
    margin: "6px 0 0",
    fontSize: "13px",
    color: "#0f766e",
    fontWeight: 700,
  },

  messages: {
    flex: 1,
    padding: "28px",
    backgroundColor: "#f8fafc",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    overflowY: "auto",
  },

  receivedMessageWrapper: {
    alignSelf: "flex-start",
    maxWidth: "60%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },

  sentMessageWrapper: {
    alignSelf: "flex-end",
    maxWidth: "60%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },

  receivedMessage: {
    backgroundColor: "#ffffff",
    color: "#0f172a",
    padding: "14px 18px",
    borderRadius: "18px",
    border: "1px solid #e2e8f0",
  },

  sentMessage: {
    backgroundColor: "#0f766e",
    color: "#ffffff",
    padding: "14px 18px",
    borderRadius: "18px",
  },

  attachmentMessageWrapper: {
    backgroundColor: "transparent",
    padding: 0,
    border: "none",
  },

  seenStatus: {
    marginTop: "4px",
    fontSize: "11px",
    color: "#64748b",
    fontWeight: 700,
  },

  messageTime: {
    marginBottom: "4px",
    fontSize: "11px",
    color: "#64748b",
    fontWeight: 700,
  },

  dateSeparator: {
    alignSelf: "center",
    backgroundColor: "#e2e8f0",
    color: "#475569",
    borderRadius: "999px",
    padding: "6px 14px",
    fontSize: "12px",
    fontWeight: 800,
    margin: "8px 0",
  },

  inputArea: {
    display: "flex",
    gap: "12px",
    padding: "20px 28px",
    borderTop: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
  },

  messageInput: {
    flex: 1,
    padding: "14px 16px",
    borderRadius: "14px",
    border: "1px solid #cbd5e1",
    outline: "none",
    fontSize: "14px",
  },

  uploadButton: {
    width: "48px",
    height: "48px",
    borderRadius: "14px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#0f766e",
    fontSize: "20px",
    fontWeight: 900,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  chatImageAttachment: {
    width: "100%",
    maxHeight: "260px",
    objectFit: "cover",
    display: "block",
  },

  imageAttachmentCard: {
    width: "360px",
    maxWidth: "100%",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "18px",
    overflow: "hidden",
    color: "#0f172a",
    textDecoration: "none",
    display: "block",
    padding: 0,
    cursor: "pointer",
    textAlign: "left",
  },

  imageFileName: {
    padding: "12px 14px",
    fontSize: "13px",
    fontWeight: 800,
    color: "#0f172a",
    backgroundColor: "#ffffff",
  },

  documentAttachmentCard: {
    width: "360px",
    maxWidth: "100%",
    backgroundColor: "#ffffff",
    border: "1px solid #d1d5db",
    borderRadius: "18px",
    overflow: "hidden",
    color: "#0f172a",
    textDecoration: "none",
    display: "block",
    padding: 0,
    cursor: "pointer",
    textAlign: "left",
  },

  documentPreviewArea: {
    width: "100%",
    height: "210px",
    backgroundColor: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },

  documentPreviewImage: {
    width: "100%",
    height: "210px",
    objectFit: "contain",
    display: "block",
    backgroundColor: "#f1f5f9",
    padding: "10px",
    boxSizing: "border-box",
  },

  previewImageArea: {
    flex: 1,
    width: "100%",
    backgroundColor: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "auto",
  },

  previewLargeImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },

  documentPreviewPlaceholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b",
  },

  documentPreviewIcon: {
    fontSize: "46px",
    marginBottom: "10px",
  },

  documentPreviewText: {
    fontSize: "14px",
    fontWeight: 800,
  },

  documentFileInfoBar: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "14px 16px",
    backgroundColor: "#ffffff",
  },

  fileIconBox: {
    width: "42px",
    height: "42px",
    borderRadius: "12px",
    backgroundColor: "#fee2e2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    flexShrink: 0,
  },

  fileInfoBox: {
    minWidth: 0,
  },

  fileNameText: {
    fontSize: "15px",
    fontWeight: 900,
    color: "#0f172a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  sendButton: {
    padding: "0 28px",
    borderRadius: "14px",
    border: "none",
    backgroundColor: "#0f766e",
    color: "#ffffff",
    fontWeight: 800,
    cursor: "pointer",
  },

  previewOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor:
      "rgba(15, 23, 42, 0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },

  previewModal: {
    width: "86vw",
    height: "86vh",
    backgroundColor: "#ffffff",
    borderRadius: "18px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow:
      "0 20px 50px rgba(0,0,0,0.25)",
  },

  previewHeader: {
    height: "58px",
    padding: "0 18px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#ffffff",
  },

  previewTitle: {
    fontSize: "15px",
    fontWeight: 900,
    color: "#0f172a",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  previewCloseButton: {
    width: "38px",
    height: "38px",
    borderRadius: "10px",
    border: "none",
    backgroundColor: "#f1f5f9",
    color: "#0f172a",
    fontSize: "18px",
    fontWeight: 900,
    cursor: "pointer",
  },

  previewFrame: {
    flex: 1,
    width: "100%",
    border: "none",
    backgroundColor: "#f8fafc",
  },
};

export default EmployeeChatPage;